import { Alert, Box, Button, Stack, Typography, useElementBreakpoints } from '@compass/core';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { Dialog, DialogContent, DialogTitle, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { useRef, useState } from 'react';
import { DASHBOARD_CARD_HEADER_SX } from '../styles/cardStyles';
import { KpiCard } from './KpiCard';
import { DashboardKpi } from './types';

interface KpiDashboardSectionProps {
    kpis: DashboardKpi[];
    loading: boolean;
    error: boolean;
    onRemove: () => void;
    onMove: (direction: 'up' | 'down') => void;
}

export const KpiDashboardSection = ({ kpis, loading, error, onRemove, onMove }: KpiDashboardSectionProps) => {
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [expanded, setExpanded] = useState(false);
    const dragStartY = useRef(0);
    const { breakpointsRef, setBreakpoints } = useElementBreakpoints();
    if (!loading && !error && kpis.length === 0) return null;
    const visibleCount = loading ? 6 : kpis.length;
    const maxColumns = setBreakpoints({ xs: 1, sm: 2, md: 3, lg: 4, xl: 5 }) ?? 1;
    const columnCount = Math.min(Math.max(visibleCount, 1), maxColumns);
    const useCompactColumns = visibleCount <= 3;

    const grid = error ? (
        <Alert severity="warning">KPI highlights are temporarily unavailable. Dashboard services are unaffected.</Alert>
    ) : (
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: `repeat(${columnCount}, minmax(0, ${useCompactColumns ? '320px' : '1fr'}))`, justifyContent: 'flex-start' }}>
            {(loading ? Array.from({ length: 6 }) : kpis).map((kpi, index) => (
                <Box key={loading ? index : `${(kpi as DashboardKpi).id}-${(kpi as DashboardKpi).viewType}`} sx={{ display: 'flex', maxWidth: visibleCount === 1 ? 320 : 'none' }}>
                    <KpiCard kpi={loading ? undefined : kpi as DashboardKpi} loading={loading} />
                </Box>
            ))}
        </Box>
    );

    return (
        <Box component="section" aria-labelledby="kpi-dashboard-heading" sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 3, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}>
            <Box
                sx={{
                    ...DASHBOARD_CARD_HEADER_SX,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    userSelect: 'none',
                    minHeight: 50,
                    py: 1,
                    px: 3.5,
                    borderBottom: 1,
                    borderColor: 'border.main',
                    boxSizing: 'border-box',
                }}
            >
                <Stack direction="row" spacing="sm" alignItems="center">
                    <Box draggable onDragStart={(event) => { dragStartY.current = event.screenY; }} onDragEnd={(event) => onMove(event.screenY >= dragStartY.current ? 'down' : 'up')} sx={{ display: 'flex', cursor: 'grab' }} aria-label="Move KPI dashboard section">
                        <DragIndicatorIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
                    </Box>
                    <Typography id="kpi-dashboard-heading" variant="h6">Year-over-Year Highlights</Typography>
                </Stack>
                <IconButton size="small" onClick={(event) => setMenuAnchor(event.currentTarget)} sx={{ color: 'white' }} aria-label="KPI section options">
                    <ExpandCircleDownIcon fontSize="small" />
                </IconButton>
            </Box>
            <Box ref={breakpointsRef} sx={{ p: { xs: 1.5, sm: 2 } }}>{grid}</Box>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                <MenuItem onClick={() => { setExpanded(true); setMenuAnchor(null); }}><ListItemIcon><OpenInFullIcon fontSize="small" /></ListItemIcon><ListItemText>Expand</ListItemText></MenuItem>
                <MenuItem onClick={() => { onRemove(); setMenuAnchor(null); }} sx={{ color: 'error.main' }}><ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon><ListItemText>Remove</ListItemText></MenuItem>
            </Menu>
            <Dialog open={expanded} onClose={() => setExpanded(false)} maxWidth={false} fullWidth PaperProps={{ sx: { width: '85vw', maxWidth: 'none', maxHeight: '80vh', borderRadius: 3 } }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'white' }}>
                    KPI: Year-over-Year Highlights
                    <Button variant="outlined" onClick={() => setExpanded(false)} sx={{ color: 'white', borderColor: 'white' }}>Close</Button>
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>{grid}</DialogContent>
            </Dialog>
        </Box>
    );
};
